import React, { Fragment, useState, useEffect } from 'react';
import { Field, FormSection } from 'redux-form';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import GraphQL from '../../../../GraphQL';
import { showNotificationAutoDestroy } from '../../../../shared/components/ShowNotification';
import SelectVirtualized from '../../../../shared/components/form/SelectVirtualized';
import { relationObjectListByParamsQuery } from '../../../../shared/queries/RelationObject';
import DateField from '../../../../shared/components/form/DateField';
import HourField from '../../../../shared/components/form/HourField';
import { TEMPLATE_APPLICATION_TYPE } from '../../../../shared/utils/constants';
import { memoTemplateListOnlyActiveByApplicationType } from '../../../../shared/queries/MemoTemplates';
import renderField from '../../../../shared/components/form/RenderField';

const OccurrenceFields = (props) => {
  const { t, showLoading, hideLoading, change, editable, changeHtmlContent } =
    props;
  const [recipients, setRecipients] = useState([]);
  const [templateOccurrences, setTemplateOccurrences] = useState([]);

  const loadRecipients = () => {
    showLoading();
    const query = relationObjectListByParamsQuery(false, false, true, true);
    GraphQL(query, {})
      .then((res) => {
        res.relationObjectListByParams = res.relationObjectListByParams.map(
          (item) => ({
            ...item,
            value: item.identifier,
            label: item.description,
          })
        );
        setRecipients(res.relationObjectListByParams);
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const loadTemplateOccurrences = () => {
    showLoading();
    const query = memoTemplateListOnlyActiveByApplicationType(
      TEMPLATE_APPLICATION_TYPE.OCCU
    );
    GraphQL(query, {})
      .then((res) => {
        res.memoTemplateListByFilter = res.memoTemplateListByFilter.map(
          (item) => ({
            ...item,
            value: item.id,
            label: item.name,
          })
        );
        setTemplateOccurrences(res.memoTemplateListByFilter);
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const onRecipientChange = (values) => {
    if (values) {
      change('recipients', values);
    } else {
      change('recipients', []);
    }
  };

  const onContentChange = (template) => {
    if (template) {
      changeHtmlContent(template.defaultBodyInHTML);
      change('title', template.defaultTitle);
      change('htmlContent', template.defaultBodyInHTML);
      change('textContent', template.defaultBody);
    } else {
      changeHtmlContent('');
      change('title', '');
      change('htmlContent', '');
      change('textContent', '');
    }
  };

  useEffect(() => {
    loadRecipients();
    loadTemplateOccurrences();
  }, []);

  return (
    <Fragment>
      <FormSection name="occurrenceHeader" className="form__section">
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.type')}</span>
          <div className="form__form-group-field">
            <Field
              name="occurrenceType"
              component={SelectVirtualized}
              isDisabled={!editable}
              options={templateOccurrences}
              onChange={onContentChange}
              isClearable
              placeholder={t('general.placeholders.select')}
            />
          </div>
        </div>
        <div className="form__form-group--2_columns">
          <div className="form__form-group">
            <span className="form__form-group-label">{t('general.date')}</span>
            <div className="form__form-group-field">
              <Field
                name="occurrenceDate"
                component={DateField}
                maxDate={new Date()}
                placeholder={t('general.placeholders.date')}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">{t('general.hour')}</span>
            <div className="form__form-group-field">
              <Field name="occurrenceHour" component={HourField} />
            </div>
          </div>
        </div>
      </FormSection>
      <div className="form__form-group">
        <span className="form__form-group-label">{t('general.involved')}</span>
        <div className="form_form-group-field">
          <Field
            name="recipients"
            isMulti
            closeMenuOnSelect={false}
            component={SelectVirtualized}
            options={recipients}
            disabled={!editable}
            onChange={onRecipientChange}
            placeholder={t('general.placeholders.select')}
          />
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">{t('general.title')}</span>
        <div className="form__form-group-field">
          <Field
            name="title"
            type="text"
            placeholder={t('communication.placeholders.occurrence-title')}
            component={renderField}
            maxLength="250"
            readOnly={!editable}
          />
        </div>
      </div>
    </Fragment>
  );
};

OccurrenceFields.propTypes = {
  t: PropTypes.func.isRequired,
  showLoading: PropTypes.func.isRequired,
  hideLoading: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  changeHtmlContent: PropTypes.func.isRequired,
  editable: PropTypes.bool.isRequired,
};

export default withTranslation('common')(OccurrenceFields);
